import { SentryEnvironment } from '@caresend/types';
import { getLimitedBreadcrumbData } from '@caresend/utils';
import { init, browserTracingIntegration, replayIntegration, addBreadcrumb, captureException, setUser } from '@sentry/vue';

/* eslint-disable no-console */
const devRegex = /https:\/\/caresend.*-dev\.web\.app\/?.*/;
const prodRegex = /https:\/\/.+\.caresend.com?\/?.*/;
const previewChannelRegex = /https:\/\/caresend.*-(dev|prod)--.*\.web\.app\/?.*/;
let isProdEnv = false;
const getSentryEnvironment = () => {
    const { origin } = window.location;
    if (prodRegex.test(origin))
        return SentryEnvironment.PROD;
    if (devRegex.test(origin))
        return SentryEnvironment.DEV;
    if (previewChannelRegex.test(origin))
        return SentryEnvironment.PREVIEW_CHANNEL;
    return SentryEnvironment.LOCAL;
};
/**
 * Pass true as 3rd argument if testing triggering Sentry locally. Ensure
 * dsn is also passed by app repo.
 */
const initSentry = (isProd, options, localTesting = false) => {
    isProdEnv = isProd;
    const environment = getSentryEnvironment();
    let allowUrls = [
        devRegex,
        prodRegex,
        previewChannelRegex,
    ];
    if (localTesting && environment === SentryEnvironment.LOCAL) {
        // Passing undefined will allow all URLs
        allowUrls = undefined;
    }
    init({
        allowUrls,
        attachProps: true,
        attachStacktrace: true,
        environment,
        integrations: [
            /**
             * TODO:
             *
             * Cody:
             *
             * I've also seen some docs that this integration works best when passing the router to this as an arg.
             * I haven't validated that this works on this version of Sentry though.
             * But could try: browserTracingIntegration({ router: getRouter() })
             *
             * I am not sure whether this is necessary however, and I believe it would
             * require us to move our initializeSentry() calls in apps to after initRouter()
             * in order to have the most accurate breadcrumbs.
             */
            browserTracingIntegration(),
            replayIntegration({
                unmask: ['.ButtonComponent *', '.toast *'],
            }),
        ],
        logErrors: !isProd,
        maxBreadcrumbs: 500,
        replaysSessionSampleRate: isProd ? 1 : 0,
        replaysOnErrorSampleRate: 1,
        tracesSampleRate: isProd ? 0.2 : 1,
        ...options,
    });
};
/**
 * Adds a basic breadcrumb to Sentry. This will show in the Sentry report
 * if an exception is encountered afterwards.
 */
const addSentryBreadcrumb = (message, category = 'generic', data) => {
    addBreadcrumb({ message, category, data: getLimitedBreadcrumbData(data) });
};
/**
 * Manually reports an exception to Sentry.
 */
const reportException = (exception) => {
    if (!isProdEnv)
        console.info('The following exception was manually reported to Sentry.');
    console.error(exception);
    captureException(exception);
};
const setSentryUser = (user) => {
    setUser(user);
};

export { addSentryBreadcrumb, initSentry, reportException, setSentryUser };
